import Axios from 'axios';
import store from '@/store';
import ApiService from '@/services/apiService';

// Use same-origin for API calls - Vue dev server proxy handles /api routing
const host = location.origin;

const httpClient = Axios.create({
  baseURL: `${host}/api/v1`,
  timeout: 5000,
  withCredentials: true,
  headers: {
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});
httpClient.defaults.cache = false;

let setAnnouncements = (tenantId, data) => {
  const now = new Date();
  store.state.app.announcements = data
    .map((announcement) => {
      // if expired set state to inactive
      const endDate = new Date(announcement.end);
      if (announcement.end && endDate < now && tenantId) {
        ApiService.tenantUpdateSystemAnnouncementState(tenantId, announcement);
        return null;
      }
      if (announcement && announcement.active) {
        return {
          ...announcement,
        };
      }
      return null;
    })
    .filter((a) => !!a);
};

const withApiKeyHeader = (apiKey, requestConfig = undefined) => {
  if (!apiKey) {
    return requestConfig;
  }

  return {
    ...(requestConfig || {}),
    headers: {
      ...((requestConfig && requestConfig.headers) || {}),
      'X-API-Key': apiKey,
    },
  };
};

export default {
  login({ commit, dispatch }, userData) {
    this.state.app.permissions = null;

    return new Promise((resolve, reject) => {
      commit('auth_request');
      httpClient
        .post('/auth/login', userData)
        .then((resp) => {
          commit('auth_success', {
            data: { ...resp.data, isApiKey: false },
          });

          // Hydrate full session state from verify, but do not roll back a successful login
          // if this best-effort call fails due to transient connectivity issues.
          dispatch('verify', { suppressLogoutOnError: true }).catch((error) => {
            console.error('Error hydrating session after successful login', error);
          });

          resolve(resp);
        })
        .catch((error) => {
          commit('logout');
          if (error.response) {
            reject(error.response);
          } else {
            reject(error);
          }
        });
    });
  },
  verify({ commit }, options = {}) {
    const normalizedOptions = typeof options === 'string' || options === null ? { apiKey: options } : options || {};
    const apiKey = normalizedOptions.apiKey || null;
    const suppressLogoutOnError = normalizedOptions.suppressLogoutOnError === true;
    const authRequestConfig = withApiKeyHeader(apiKey, {
      params: { domain: window.location.hostname },
    });
    const announcementsRequestConfig = withApiKeyHeader(apiKey);

    return httpClient
      .get('/auth/verify', authRequestConfig)
      .then(({ data }) => {
        if (!data.user || !data.isAuthenticated) {
          throw new Error('Auth error');
        }

        commit('app/setFeatureFlags', data.featureFlags.flags, { root: true });

        const tenantId = data.tenantId;
        return httpClient
          .get('/user/own/announcements', announcementsRequestConfig)
          .then((announcements) => {
            if (announcements && announcements.data.length) {
              setAnnouncements(tenantId, announcements.data);
            }
            return data.user;
          })
          .catch((err) => {
            console.error('Error getting announcements', err);
          })
          .finally(() => {
            commit('auth_success', {
              data: { ...data, isApiKey: !!apiKey },
            });
          });
      })
      .catch((error) => {
        if (!suppressLogoutOnError) {
          commit('logout');
        }
        throw error;
      });
  },
  impersonate({ commit }, userId) {
    delete this.state.app.permissions;
    return httpClient.post('/auth/impersonate', { userId }).then(({ data }) => {
      if (!data.user) {
        throw new Error('Impersonate error');
      }
      commit('auth_success', {
        data: { ...data, isImpersonating: true },
      });
    });
  },
  restoreOriginal({ commit }) {
    return httpClient
      .post('/auth/restore')
      .then(({ data }) => {
        if (!data.user) {
          throw new Error('Auth error during restoration');
        }

        delete store.state.app.permissions;

        commit('app/setFeatureFlags', data.featureFlags.flags, { root: true });

        commit('auth_success', {
          data: { ...data, isImpersonating: false },
        });
      })
      .catch((error) => {
        console.error('Error restoring original user:', error);
        throw error;
      });
  },
  logout({ commit }, options = {}) {
    const shouldReload = options.reload !== false;
    return httpClient.get('/auth/logout').finally(() => {
      commit('logout');
      if (shouldReload) {
        window.location.reload();
      }
    });
  },
};
